import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  IconButton,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';

/**
 * Certificate metadata structure for parsing
 * This defines the expected fields that the PDF parser will try to extract
 * Fields match the actual CertificateInfoInputs form
 */
export const CERTIFICATE_METADATA_SCHEMA = {
  // Basic certificate information
  certificateName: {
    label: 'ID Certificat',
    type: 'text',
    required: true,
  },
  certificateType: {
    label: 'Type de Certificat',
    type: 'select',
    required: true,
    options: [
      { value: 'gauged_container', label: 'Conteneur Jaugé' },
      { value: 'tanker_truck', label: 'Camion Citerne' },
    ],
  },

  // Tank information
  containerId: {
    label: 'ID Récipient',
    type: 'text',
    required: true,
  },
  cellarId: {
    label: 'ID Chai',
    type: 'text',
    required: true,
  },

  // Organization information
  owner: {
    label: 'Nom de l\'Organisation Propriétaire',
    type: 'text',
    required: true,
  },
  gaugerName: {
    label: 'Prestataire de jaugeage',
    type: 'text',
    required: false,
  },

  // Volume information
  containerCapacity: {
    label: 'Capacité (m³)',
    type: 'number',
    required: true,
  },
  heightUnit: {
    label: 'Unité de hauteur',
    type: 'text',
    required: false,
  },
  volumeUnit: {
    label: 'Unité de volume',
    type: 'text',
    required: false,
  },
  nominalVolume: {
    label: 'Volume Nominal (m³)',
    type: 'number',
    required: false,
  },

  // Dates
  gaugingDate: {
    label: 'Date de la vérification',
    type: 'date',
    required: true,
  },
  validityDate: {
    label: 'Date de Validité',
    type: 'date',
    required: true,
  },
};

export default function CertificateMetadataForm({
  initialData = {},
  onDataChange,
  extractedData = null,
  disabled = false,
  collapsible = true,
}) {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [expanded, setExpanded] = useState(true);

  // Initialize form data
  useEffect(() => {
    const initialFormData = { ...initialData };

    // If we have extracted data, try to populate fields
    if (extractedData?.metadata) {
      const backendMetadata = extractedData.metadata;

      // Map backend field names to form field names (with backward compatibility)
      const fieldMapping = {
        // Direct mappings (backend field names match schema)
        certificateName: backendMetadata.certificateName,
        containerId: backendMetadata.containerId || backendMetadata.tankName,
        cellarId: backendMetadata.cellarId || backendMetadata.cellarName,
        owner: backendMetadata.owner || backendMetadata.ownerOrganizationName || backendMetadata.proprietaire,
        gaugerName: backendMetadata.gaugerName || backendMetadata.gaugingProviderName,
        containerCapacity: backendMetadata.containerCapacity || backendMetadata.capacity,
        heightUnit: backendMetadata.heightUnit || backendMetadata.heightUnits,
        volumeUnit: backendMetadata.volumeUnit || backendMetadata.volumeUnits,
        nominalVolume: backendMetadata.nominalVolume || backendMetadata.nominalVolumes,
        gaugingDate: backendMetadata.gaugingDate || backendMetadata.gaugeDate,
        validityDate: backendMetadata.validityDate || backendMetadata.expirationDate,
      };

      Object.keys(CERTIFICATE_METADATA_SCHEMA).forEach((key) => {
        if (fieldMapping[key] && !initialFormData[key]) {
          initialFormData[key] = fieldMapping[key];
        }
      });
    }

    setFormData(initialFormData);
  }, [initialData, extractedData]);

  // Validate form data
  const validateForm = (data) => {
    const newErrors = {};

    Object.keys(CERTIFICATE_METADATA_SCHEMA).forEach((key) => {
      const field = CERTIFICATE_METADATA_SCHEMA[key];
      const value = data[key];

      if (field.required && (!value || value === '')) {
        newErrors[key] = `${field.label} est requis`;
      }

      if (field.type === 'number' && value && isNaN(parseFloat(value))) {
        newErrors[key] = `${field.label} doit être un nombre valide`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = (field, value) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);

    if (validateForm(newData)) {
      onDataChange?.(newData);
    }
  };

  const renderField = (fieldKey, fieldSchema) => {
    const value = formData[fieldKey] || '';
    const error = errors[fieldKey];

    switch (fieldSchema.type) {
      case 'select':
        return (
          <FormControl fullWidth error={!!error} disabled={disabled}>
            <InputLabel>{fieldSchema.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
              label={fieldSchema.label}
            >
              {fieldSchema.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'number':
        return (
          <TextField
            fullWidth
            label={fieldSchema.label}
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(fieldKey, parseFloat(e.target.value) || '')}
            error={!!error}
            helperText={error}
            disabled={disabled}
            inputProps={{ step: '0.01' }}
          />
        );

      case 'date':
        return (
          <TextField
            fullWidth
            label={fieldSchema.label}
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
            error={!!error}
            helperText={error}
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
          />
        );

      default:
        return (
          <TextField
            fullWidth
            label={fieldSchema.label}
            value={value}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
            error={!!error}
            helperText={error}
            disabled={disabled}
          />
        );
    }
  };

  const content = (
    <Box>
      {/* All fields in a single compact grid */}
      <Grid container spacing={1.5}>
        {Object.keys(CERTIFICATE_METADATA_SCHEMA).map((field) => (
          <Grid item xs={12} sm={6} key={field}>
            {renderField(field, CERTIFICATE_METADATA_SCHEMA[field])}
          </Grid>
        ))}
      </Grid>

      {/* Validation Summary */}
      {Object.keys(errors).length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2">
            Veuillez corriger les erreurs suivantes :
          </Typography>
          {Object.entries(errors).map(([field, error]) => (
            <Typography key={field} variant="body2">
              •
              {' '}
              {error}
            </Typography>
          ))}
        </Alert>
      )}
    </Box>
  );

  if (!collapsible) {
    return content;
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title="Métadonnées du Certificat"
        action={(
          <IconButton
            onClick={() => setExpanded(!expanded)}
            aria-label="expand"
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      />
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <CardContent>
          {content}
        </CardContent>
      </Collapse>
    </Card>
  );
}
